import React, { useCallback, useEffect, useState } from "react";
import { StepIndicator } from "../../../components/marketing/campaign/indicator";
import { CampaignSetup } from "../../../components/marketing/campaign/campaign-details/campaign-setup";
import { CampaignData } from "../../../components/types";
import { useNavigate } from "react-router-dom";
import { instagramSteps, steps } from "./data";
import InstagramFlow from "./instagram";
import { CHANNEL_OPTIONS } from "../../../components/marketing/campaign/campaign-details/campaign-setup";
import useRequest from "../../../components/hook/use-req";
import { toast } from "sonner";
import {
  SingleAudienceResponse,
  SingleCampaignResponse,
  WalletBalanceData,
} from "../campaign/types";
import { ArrowLeft } from "lucide-react";
import BroadcastDraft from "../../../components/marketing/campaign/broadcast-draft";
import ScheduleLaunch from "../../../components/marketing/campaign/shedule-launch";
import { AIContentCreation } from "../../../components/marketing/campaign/content-creation";
import ScrollToTop from "../../../routes/scroll-top";
import { Audience } from "./types";

// Set by Audience Studio's "Activate" button (views/unify-pages/audiences/
// index.tsx) right before it navigates here, after saving the recommendation
// as a real segment via POST /segments/planner/random — read once on mount,
// same convention as draftCampaignId/draftAudienceId below.
function readDraftSegment(): Audience | null {
  const raw = localStorage.getItem("draftSegment");
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed?.id) return null;
    return {
      id: parsed.id,
      audience_id: parsed.id,
      name: parsed.name || "Planner audience",
      contacts: parsed.contacts || 0,
      uploadDate: new Date().toLocaleDateString(),
      status: "completed",
      validCount: parsed.validCount,
      invalidCount: parsed.invalidCount,
    };
  } catch {
    return null;
  }
}

const SMSCampaignFlow = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("authToken");
  const draftCampaignId = localStorage.getItem("draftCampaignId");
  const draftAudienceId = localStorage.getItem("draftAudienceId");
  const [draftChannel] = useState(() => localStorage.getItem("draftChannel"));
  const [draftSegment] = useState(readDraftSegment);
  const [singleCampaign, setSingleCampaign] =
    useState<SingleCampaignResponse | null>(null);
  const [currentStep, setCurrentStep] = useState(2);
  const [walletBallance, setWlletBallance] = useState<WalletBalanceData | null>(
    null,
  );
  const [audienceById, setAudienceById] =
    useState<SingleAudienceResponse | null>(null);
  const [campaignData, setCampaignData] = useState<CampaignData>({
    sender_name: "",
    audienceId: "",
    template_id: "",
    campaign_name: "",
    campaign_channel: draftChannel || "",
    campaign_type: "",
    previewText: "",
    html_text: "",
    plain_text: "",
    email_header: "",
    emailCss: "",
    date: "",
    link: "",
    subject: "",
    audioUrl: "",
    smsSegment: 0,
    campaign_goal: "",
    broadcast_tags: [],
    timezone: "",
    business_category: "",
    contacts: 0,
    typedNumbers: [],
    messages: "",
    scheduled_start: "",
    audienceSize: "",
    aiSettings: {},
    id: 0,
    countryCounts: 0,
    uploadDate: "",
    totalRecords: 0,
    validRecords: 0,
    invalidRecords: 0,
    duplicateRecords: 0,
      uploadedFileName: "",
    preview: [],
    data: [],
    send_now: false,
    voice_id: "",
    selectedAudiences: draftSegment ? [draftSegment] : [],
    uploadAudienceId: null,
    manualAudienceId: null,
    tags: [],
    uploadedContacts: 0,
    manualValidRecords: 0,
    campaign_id: "",
    campaign_size: 0,
    estimated_cost: 0,
    cost_per_unit: 0,
    audiences: [],
    mediaFile: null,
    mediaFileUrl: "",
  });

  const { makeRequest: getCampaignById } = useRequest(
    `/campaign/details/${draftCampaignId}`,
    "GET",
    {
      Authorization: `Bearer ${token}`,
    },
  );

  const { makeRequest: getAudienceByid } = useRequest(
    `/audience/${draftAudienceId}`,
    "GET",
    {
      Authorization: `Bearer ${token}`,
    },
  );
  const { makeRequest: WalletBalance } = useRequest("/wallet/balance", "GET", {
    Authorization: `Bearer ${token}`,
  });

  function backToCampaign() {
    navigate("/campaign");
    localStorage.removeItem("draftCampaignId");
    localStorage.removeItem("draftAudienceId");
    localStorage.removeItem("audienceId");
    window.location.reload();
  }

  // Validation states for each step
  const [stepValidation, setStepValidation] = useState({
    step1Valid: false,
    step2Valid: false,
    step3Valid: false,
    step4Valid: false,
  });

  useEffect(() => {
    if (!token) {
      window.location.reload();
    }
  }, [token, navigate]);

  // One-shot — consumed into campaignData's initial state above, so clear it
  // immediately rather than leaving it to leak into an unrelated later visit.
  useEffect(() => {
    if (draftChannel) localStorage.removeItem("draftChannel");
    if (draftSegment) localStorage.removeItem("draftSegment");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (draftCampaignId) {
        const [response] = await getCampaignById();
        setSingleCampaign(response?.response);
      }
    };

    fetchData();
  }, [draftCampaignId]);

  useEffect(() => {
    const fetchData = async () => {
      if (draftAudienceId) {
        const [response] = await getAudienceByid();
        setAudienceById(response);
      }
    };
    fetchData();
  }, [draftAudienceId]);

  // After your existing useEffects, add these:

  // Populate campaign data from draft
  useEffect(() => {
    if (singleCampaign) {
      setCampaignData((prev) => ({
        ...prev,
        campaign_id: singleCampaign.campaign_id || prev.campaign_id,
        campaign_name: singleCampaign.campaign_name || prev.campaign_name,
        campaign_channel:
          singleCampaign.campaign_channel?.toLowerCase() ||
          prev.campaign_channel,
        campaign_type: singleCampaign.campaign_type || prev.campaign_type,
        campaign_goal: singleCampaign.campaign_goal || prev.campaign_goal,
        broadcast_tags: singleCampaign.broadcast_tags || prev.broadcast_tags,
        sender_name: singleCampaign.sender_name || prev.sender_name,
        scheduled_start: singleCampaign.scheduled_start || prev.scheduled_start,
        send_now: singleCampaign.send_now || prev.send_now,
        timezone: singleCampaign.timezone || prev.timezone,
        business_category:
          singleCampaign.business_category || prev.business_category,
        messages: singleCampaign.content?.message || prev.messages,
        link: singleCampaign.content?.url || prev.link,
        campaign_size: singleCampaign.campaign_size || prev.campaign_size,
        estimated_cost: singleCampaign.estimated_cost || prev.estimated_cost,
        cost_per_unit: singleCampaign.cost_per_unit || prev.cost_per_unit,
        audienceId: singleCampaign.audience_id || prev.audienceId,
        // selectedAudiences: singleCampaign.audiences || prev.selectedAudiences,
      }));
    }
  }, [singleCampaign]);

  // Populate audience data from draft
  useEffect(() => {
    if (audienceById) {
      setCampaignData((prev) => ({
        ...prev,
        contacts: audienceById.contacts?.length || prev.contacts,
        totalRecords: audienceById.total_count || prev.totalRecords,
        validRecords: audienceById.unique_count || prev.validRecords,
        invalidRecords: audienceById.invalids_count || prev.invalidRecords,
        duplicateRecords:
          audienceById.duplicates_count || prev.duplicateRecords,
        audienceId: audienceById.audience_id || prev.audienceId,
      }));
    }
  }, [audienceById]);

  const fetchWalletBalance = async () => {
    const [response] = await WalletBalance();
    setWlletBallance(response?.data);
  };

  useEffect(() => {
    fetchWalletBalance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Submit handlers for each step
  const handleStep1Submit = (data: Partial<CampaignData>) => {
    const formattedData = {
      ...data,
      date: data.date ? data.date.toISOString() : "",
    };

    setCampaignData((prev) => ({ ...prev, ...formattedData }));
    setStepValidation((prev) => ({ ...prev, step1Valid: true }));
    setCurrentStep(3);
  };

  const handleStep4Submit = (data: Partial<CampaignData>) => {
    setCampaignData((prev) => ({ ...prev, ...data }));
    navigate("/campaign");
    // toast.success("Campaign setup created successfully!");
  };

  // Validation handlers to update step validation status
  const handleStep1Validation = useCallback((isValid: boolean) => {
    setStepValidation((prev) => {
      if (prev.step1Valid === isValid) return prev;
      return { ...prev, step1Valid: isValid };
    });
  }, []);

  const handleStep3Validation = useCallback((isValid: boolean) => {
    setStepValidation((prev) => {
      if (prev.step3Valid === isValid) return prev;
      return { ...prev, step3Valid: isValid };
    });
  }, []);

  // Instagram runs its own 5-step path; every other channel keeps the
  // standard 4 steps. The channel is picked in Details, so step 1 and the
  // header are shared until then.
  const isInstagram = campaignData.campaign_channel === "instagram";
  const activeSteps = isInstagram ? instagramSteps : steps;

  const handleChannelChange = (channel: string) => {
    setCampaignData((prev) => (prev.campaign_channel === channel ? prev : { ...prev, campaign_channel: channel }));
  };

  const renderStep = () => {
    if (isInstagram && currentStep >= 2) {
      return (
        <InstagramFlow
          currentStep={currentStep}
          setCurrentStep={setCurrentStep}
          channelOptions={CHANNEL_OPTIONS}
          onChannelChange={handleChannelChange}
          onExit={backToCampaign}
          existingCampaignId={draftCampaignId || undefined}
          existingContent={singleCampaign?.content}
        />
      );
    }
    switch (currentStep) {
      case 1:
        return <BroadcastDraft />;
      case 2:
        return (
          <CampaignSetup
            campaignData={campaignData}
            singleCampaign={singleCampaign}
            setCampaignData={setCampaignData}
            onSubmit={handleStep1Submit}
            onValidation={handleStep1Validation}
            audienceById={audienceById}
            onChannelChange={handleChannelChange}
          />
        );
      case 3:
        return (
          <AIContentCreation
            campaignData={campaignData}
            singleCampaign={singleCampaign}
            setCampaignData={setCampaignData}
            onSubmit={(data) => {
              setCampaignData((prev) => ({ ...prev, ...data }));
              setStepValidation((prev) => ({ ...prev, step3Valid: true }));
              setCurrentStep(4);
            }}
            onValidation={handleStep3Validation}
            onBack={() => setCurrentStep(2)}
          />
        );
      case 4:
        return (
          <ScheduleLaunch
            campaignData={campaignData}
            walletBallance={walletBallance}
            singleCampaign={singleCampaign}
            setCampaignData={setCampaignData}
            onSubmit={handleStep4Submit}
            onBack={() => setCurrentStep(3)}
            onWalletFunded={fetchWalletBalance}
          />
        );
      default:
        return (
          <CampaignSetup
            campaignData={campaignData}
            singleCampaign={singleCampaign}
            setCampaignData={setCampaignData}
            onSubmit={handleStep1Submit}
            onValidation={handleStep1Validation}
            audienceById={audienceById}
            onChannelChange={handleChannelChange}
          />
        );
    }
  };

  return (
    <>
      <div className="min-h-screen flex flex-col">
        <div className="bg-white dark:bg-gray-900 rounded-lg px-6 py-5 sticky top-0 z-20 border-b border-gray-100 dark:border-gray-800">
          <h1 className="flex text-[20px] font-medium">
            <button className="flex gap-5" onClick={backToCampaign}>
              <ArrowLeft className="text-gray-700 dark:text-gray-300" />
              <span className="text-[#7B7B7B] font-semibold dark:text-gray-500">All Broadcasts</span>
            </button>
            <span className="text-[#7B7B7B] font-semibold dark:text-gray-500">/</span> <strong className="text-[#3A3428] dark:text-gray-100">New Broadcast</strong>
          </h1>

          {/* Mobile: compact step progress — the full step row below needs
              real horizontal space for its connector lines, so it's hidden
              here rather than left to wrap/stack awkwardly. */}
          <div className="lg:hidden my-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                Step {currentStep} of {activeSteps.length}: {activeSteps.find((s) => s.id === currentStep)?.title}
              </span>
              <span className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500">
                {Math.round((currentStep / activeSteps.length) * 100)}%
              </span>
            </div>
            <div className="h-1.5 w-full bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
              <div
                className="h-full bg-indigo-600 rounded-full transition-all duration-300"
                style={{ width: `${(currentStep / activeSteps.length) * 100}%` }}
              />
            </div>
          </div>

          {/* Desktop: full step row */}
          <div className="hidden lg:flex gap-7 my-5">
            {activeSteps.map((step) => (
              <div
                key={step.id}
                className="cursor-pointer w-full"
                onClick={() => {
                  if (step.id <= currentStep) {
                    setCurrentStep(step.id);
                  }
                }}
              >
                <StepIndicator
                  step={step}
                  isActive={currentStep === step.id}
                  isCompleted={currentStep > step.id}
                />
              </div>
            ))}
          </div>
        </div>

        {/*  Scrollable Content */}
        <div className="flex-1 overflow-y-auto bg-slate-100 dark:bg-transparent p-6">
          <div className="bg-slate-100 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-6">
            {renderStep()}
          </div>
        </div>
      </div>

      <ScrollToTop />
    </>
  );
};

export default SMSCampaignFlow;
